<div class="standards-container">

    <div class="page-header">
        <h1>UI Standards & Component Library</h1>
        <p class="subtitle">Comprehensive documentation of all UI components, patterns, and standards used in Lab
            Partner</p>
    </div>

    <mat-tab-group animationDuration="300ms" class="standards-tabs">

        <!-- TAB 0: Overview -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">info</mat-icon>
                Overview
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Introduction & Principles</h2>
                    <p>This documentation serves as the single source of truth for UI development in Lab Partner. All
                        new features must follow these standards to ensure consistency and maintainability.</p>

                    <div class="principle-grid">
                        <div class="principle-card">
                            <mat-icon color="primary">library_books</mat-icon>
                            <h3>Framework Preference</h3>
                            <p>Use <strong>Angular Material</strong> components as the primary UI library. PrimeNG
                                should only be used for complex data grids requiring advanced features.</p>
                        </div>
                        <div class="principle-card">
                            <mat-icon color="primary">text_fields</mat-icon>
                            <h3>Typography</h3>
                            <p>Global font: <strong>Roboto, 'Helvetica Neue', sans-serif</strong><br>Base size:
                                <strong>14px</strong>
                            </p>
                        </div>
                        <div class="principle-card">
                            <mat-icon color="primary">accessibility</mat-icon>
                            <h3>Accessibility</h3>
                            <p>All components must be keyboard navigable and screen reader friendly.</p>
                        </div>
                    </div>

                    <div class="warning-block" style="margin-top: 24px;">
                        <strong>⚠️ Current Technical Debt:</strong> The application currently mixes Angular Material and
                        PrimeNG components inconsistently. This documentation highlights these issues with warnings and
                        provides migration recommendations.
                    </div>

                    <h3 style="margin-top: 32px;">Component Library Summary</h3>
                    <div class="stats-grid">
                        <div class="stat-card">
                            <div class="stat-number">15+</div>
                            <div class="stat-label">Component Types</div>
                        </div>
                        <div class="stat-card warning">
                            <div class="stat-number">5</div>
                            <div class="stat-label">Components with Conflicts</div>
                        </div>
                        <div class="stat-card">
                            <div class="stat-number">2</div>
                            <div class="stat-label">UI Libraries</div>
                        </div>
                        <div class="stat-card">
                            <div class="stat-number">20+</div>
                            <div class="stat-label">Pages Using Standards</div>
                        </div>
                    </div>

                    <h3 style="margin-top: 48px;">Best Practices</h3>
                    <div class="best-practices-grid">
                        <div class="practice-card">
                            <div class="practice-number">1</div>
                            <h4>Consistency First</h4>
                            <p>Always check existing components before creating new ones. Reuse patterns that already
                                exist in
                                the application.</p>
                        </div>
                        <div class="practice-card">
                            <div class="practice-number">2</div>
                            <h4>Material Over PrimeNG</h4>
                            <p>For new features, always use Material components unless you specifically need advanced
                                grid
                                features.</p>
                        </div>
                        <div class="practice-card">
                            <div class="practice-number">3</div>
                            <h4>Avoid Custom Components</h4>
                            <p>Don't create custom components that duplicate library functionality. Use standard library
                                components.</p>
                        </div>
                        <div class="practice-card">
                            <div class="practice-number">4</div>
                            <h4>Accessibility Matters</h4>
                            <p>Ensure all interactive elements are keyboard accessible and have proper ARIA labels.</p>
                        </div>
                        <div class="practice-card">
                            <div class="practice-number">5</div>
                            <h4>Test Responsiveness</h4>
                            <p>Test your UI at different screen sizes. Material components are responsive by default.
                            </p>
                        </div>
                        <div class="practice-card">
                            <div class="practice-number">6</div>
                            <h4>Follow Naming Conventions</h4>
                            <p>Use clear, descriptive names for components, variables, and CSS classes. Follow Angular
                                style
                                guide.</p>
                        </div>
                    </div>

                    <h3 style="margin-top: 48px;">Quick Migration Guide</h3>
                    <div class="migration-guide">
                        <div class="migration-step">
                            <div class="step-header">
                                <mat-icon>swap_horiz</mat-icon>
                                <h4>PrimeNG Button → Material Button</h4>
                            </div>
                            <div class="migration-content">
                                <div class="before-after">
                                    <div class="before">
                                        <span class="label">Before (PrimeNG)</span>
                                        <code>&lt;button pButton label="Click Me"&gt;&lt;/button&gt;</code>
                                    </div>
                                    <mat-icon class="arrow">arrow_forward</mat-icon>
                                    <div class="after">
                                        <span class="label">After (Material)</span>
                                        <code>&lt;button mat-raised-button&gt;Click Me&lt;/button&gt;</code>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="migration-step">
                            <div class="step-header">
                                <mat-icon>swap_horiz</mat-icon>
                                <h4>PrimeNG Input → Material Input</h4>
                            </div>
                            <div class="migration-content">
                                <div class="before-after">
                                    <div class="before">
                                        <span class="label">Before (PrimeNG)</span>
                                        <code>&lt;input pInputText placeholder="Name"&gt;</code>
                                    </div>
                                    <mat-icon class="arrow">arrow_forward</mat-icon>
                                    <div class="after">
                                        <span class="label">After (Material)</span>
                                        <code>&lt;mat-form-field&gt;&lt;input matInput placeholder="Name"&gt;&lt;/mat-form-field&gt;</code>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="migration-step">
                            <div class="step-header">
                                <mat-icon>swap_horiz</mat-icon>
                                <h4>PrimeNG Select → Material Select</h4>
                            </div>
                            <div class="migration-content">
                                <div class="before-after">
                                    <div class="before">
                                        <span class="label">Before (PrimeNG)</span>
                                        <code>&lt;p-select [options]="items"&gt;&lt;/p-select&gt;</code>
                                    </div>
                                    <mat-icon class="arrow">arrow_forward</mat-icon>
                                    <div class="after">
                                        <span class="label">After (Material)</span>
                                        <code>&lt;mat-select&gt;&lt;mat-option *ngFor="let item of items"&gt;&lt;/mat-select&gt;</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 1: Material vs PrimeNG -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">compare_arrows</mat-icon>
                Material vs PrimeNG
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Angular Material vs PrimeNG - Comparative Analysis</h2>

                    <div class="warning-block">
                        <strong>⚠️ Current State:</strong> Lab Partner uses both libraries inconsistently, creating
                        technical debt, larger bundle size, and inconsistent UX patterns. This analysis evaluates the
                        feasibility of complete migration to Material.
                    </div>

                    <h3>Components Currently in Use</h3>

                    <div class="comparison-grid">
                        <div class="library-column material">
                            <div class="library-header">
                                <mat-icon>check_circle</mat-icon>
                                <h4>Angular Material</h4>
                                <span class="usage-badge">Primary Library</span>
                            </div>
                            <ul class="component-list">
                                <li><mat-icon>check</mat-icon> <strong>Buttons:</strong> mat-button, mat-raised-button,
                                    mat-icon-button, mat-fab</li>
                                <li><mat-icon>check</mat-icon> <strong>Form Fields:</strong> mat-form-field, matInput
                                    (limited use)</li>
                                <li><mat-icon>check</mat-icon> <strong>Select:</strong> mat-select, mat-option</li>
                                <li><mat-icon>check</mat-icon> <strong>Checkbox:</strong> mat-checkbox</li>
                                <li><mat-icon>check</mat-icon> <strong>Radio:</strong> mat-radio-button, mat-radio-group
                                </li>
                                <li><mat-icon>check</mat-icon> <strong>Toggle:</strong> mat-slide-toggle</li>
                                <li><mat-icon>check</mat-icon> <strong>Datepicker:</strong> mat-datepicker</li>
                                <li><mat-icon>check</mat-icon> <strong>Table:</strong> mat-table (18+ components)</li>
                                <li><mat-icon>check</mat-icon> <strong>Paginator:</strong> mat-paginator</li>
                                <li><mat-icon>check</mat-icon> <strong>Dialog:</strong> MatDialog (all dialogs)</li>
                                <li><mat-icon>check</mat-icon> <strong>Toolbar:</strong> mat-toolbar</li>
                                <li><mat-icon>check</mat-icon> <strong>Progress:</strong> mat-spinner, mat-progress-bar
                                </li>
                                <li><mat-icon>check</mat-icon> <strong>Badge:</strong> matBadge</li>
                                <li><mat-icon>check</mat-icon> <strong>Icons:</strong> mat-icon (Material Icons)</li>
                                <li><mat-icon>check</mat-icon> <strong>Snackbar:</strong> MatSnackBar</li>
                            </ul>
                        </div>

                        <div class="library-column primeng">
                            <div class="library-header">
                                <mat-icon>warning</mat-icon>
                                <h4>PrimeNG</h4>
                                <span class="usage-badge warning">Mixed Use/Legacy</span>
                            </div>
                            <ul class="component-list">
                                <li><mat-icon>warning</mat-icon> <strong>Button:</strong> pButton (4 components -
                                    migratable)</li>
                                <li><mat-icon>error</mat-icon> <strong>InputText:</strong> pInputText (15+ components -
                                    high priority)</li>
                                <li><mat-icon>error</mat-icon> <strong>Select:</strong> p-select (10+ components - high
                                    priority)</li>
                                <li><mat-icon>warning</mat-icon> <strong>Checkbox:</strong> p-checkbox (2 components -
                                    migratable)</li>
                                <li><mat-icon>warning</mat-icon> <strong>Radio:</strong> p-radioButton (1 component -
                                    migratable)</li>
                                <li><mat-icon>check_circle</mat-icon> <strong>Table:</strong> p-table (3 components -
                                    JUSTIFIED for complex grids)</li>
                                <li><mat-icon>info</mat-icon> <strong>Toast:</strong> p-toast (notifications)</li>
                                <li><mat-icon>info</mat-icon> <strong>BlockUI:</strong> BlockUIModule (loading overlay)
                                </li>
                                <li><mat-icon>info</mat-icon> <strong>Galleria:</strong> GalleriaModule (image gallery -
                                    no direct equivalent)</li>
                                <li><mat-icon>warning</mat-icon> <strong>Listbox:</strong> ListboxModule (migratable to
                                    mat-selection-list)</li>
                                <li><mat-icon>warning</mat-icon> <strong>MultiSelect:</strong> MultiSelectModule
                                    (migratable to mat-select multiple)</li>
                            </ul>
                        </div>
                    </div>

                    <h3 style="margin-top: 48px;">Complete Migration Analysis</h3>

                    <div class="migration-analysis">
                        <div class="analysis-card feasible">
                            <div class="card-header">
                                <mat-icon>check_circle</mat-icon>
                                <h4>✅ Migración Factible (Sin Pérdida de Funcionalidad)</h4>
                            </div>
                            <div class="card-content">
                                <p><strong>Components that can be migrated 1:1 to Material:</strong></p>
                                <ul>
                                    <li><strong>pButton → mat-button:</strong> Material offers more variants (raised,
                                        stroked,
                                        flat, icon, fab)</li>
                                    <li><strong>pInputText → matInput:</strong> Material tiene mejor integración con
                                        validación
                                        y hints</li>
                                    <li><strong>p-select → mat-select:</strong> Equivalent functionality con mejor UX
                                    </li>
                                    <li><strong>p-checkbox → mat-checkbox:</strong> Identical functionality</li>
                                    <li><strong>p-radioButton → mat-radio-button:</strong> Identical functionality</li>
                                    <li><strong>ListboxModule → mat-selection-list:</strong> Material ofrece mejor
                                        accesibilidad
                                    </li>
                                    <li><strong>MultiSelectModule → mat-select [multiple]:</strong> Funcionalidad
                                        equivalente
                                    </li>
                                </ul>
                                <div class="impact-badge success">
                                    <strong>Impact:</strong> Mejora en consistencia, reducción de bundle size (~200KB),
                                    mejor
                                    UX
                                </div>
                            </div>
                        </div>

                        <div class="analysis-card partial">
                            <div class="card-header">
                                <mat-icon>info</mat-icon>
                                <h4>⚠️ Migración con Alternativas (Requiere Evaluación)</h4>
                            </div>
                            <div class="card-content">
                                <p><strong>Components requiring alternative solutions:</strong></p>
                                <ul>
                                    <li><strong>p-toast → MatSnackBar:</strong>
                                        <ul>
                                            <li>✅ Material has snackbar for notifications</li>
                                            <li>⚠️ Different API and positioning</li>
                                            <li>💡 Requires refactoring toast service calls</li>
                                        </ul>
                                    </li>
                                    <li><strong>BlockUIModule → Custom Overlay:</strong>
                                        <ul>
                                            <li>✅ Material has Overlay CDK</li>
                                            <li>⚠️ Requires custom BlockUI pattern implementation</li>
                                            <li>💡 Effort: ~4-8 hours of development</li>
                                        </ul>
                                    </li>
                                    <li><strong>GalleriaModule → Custom Gallery:</strong>
                                        <ul>
                                            <li>❌ Material has no native gallery component</li>
                                            <li>✅ Options: ngx-gallery, photoswipe, or custom with Material Dialog</li>
                                            <li>💡 Effort: ~8-16 hours of development/integración</li>
                                        </ul>
                                    </li>
                                </ul>
                                <div class="impact-badge warning">
                                    <strong>Impact:</strong> Requires additional development but is feasible
                                </div>
                            </div>
                        </div>

                        <div class="analysis-card keep">
                            <div class="card-header">
                                <mat-icon>check_circle</mat-icon>
                                <h4>✅ Mantener PrimeNG (Justificado)</h4>
                            </div>
                            <div class="card-content">
                                <p><strong>Components where PrimeNG offers significant advantages:</strong></p>
                                <ul>
                                    <li><strong>p-table (Results Details, Savanna Config):</strong>
                                        <ul>
                                            <li>✅ Column freezing (freeze columns)</li>
                                            <li>✅ Column reordering (reorder columns)</li>
                                            <li>✅ Column resizing (resize columns)</li>
                                            <li>✅ Advanced filtering per column</li>
                                            <li>✅ Virtual scrolling for large datasets</li>
                                            <li>✅ Export to CSV/Excel built-in</li>
                                            <li>❌ Material table requires manual implementation of these features</li>
                                        </ul>
                                    </li>
                                </ul>
                                <div class="impact-badge info">
                                    <strong>Recommendation:</strong> Mantener p-table only para grids complejos (3
                                    componentes
                                    actuales)
                                </div>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 48px;">Recommended Migration Plan</h3>

                    <div class="migration-plan">
                        <div class="phase">
                            <div class="phase-header phase-1">
                                <span class="phase-number">Phase 1</span>
                                <h4>Quick Wins - Simple Components</h4>
                                <span class="effort">Esfuerzo: 2-3 days</span>
                            </div>
                            <div class="phase-content">
                                <ul>
                                    <li>✅ Migrate all <code>pButton</code> a <code>mat-button</code> (4 files)
                                    </li>
                                    <li>✅ Migrar <code>p-checkbox</code> y <code>p-radioButton</code> (3 files)</li>
                                    <li>✅ Migrar <code>ListboxModule</code> y <code>MultiSelectModule</code></li>
                                </ul>
                                <p><strong>Benefit:</strong> Immediate reduction of ~50KB en bundle, mayor
                                    consistencia visual
                                </p>
                            </div>
                        </div>

                        <div class="phase">
                            <div class="phase-header phase-2">
                                <span class="phase-number">Phase 2</span>
                                <h4>Forms - Inputs and Selects</h4>
                                <span class="effort">Esfuerzo: 1-2 weeks</span>
                            </div>
                            <div class="phase-content">
                                <ul>
                                    <li>⚠️ Migrar <code>pInputText</code> a <code>matInput</code> (15+ files)</li>
                                    <li>⚠️ Migrar <code>p-select</code> a <code>mat-select</code> (10+ files)</li>
                                    <li>⚠️ Update validations and error handling</li>
                                    <li>⚠️ Exhaustive testing of all forms</li>
                                </ul>
                                <p><strong>Benefit:</strong> UX consistente en todos los forms, mejor accesibilidad,
                                    reducción
                                    de ~100KB</p>
                            </div>
                        </div>

                        <div class="phase">
                            <div class="phase-header phase-3">
                                <span class="phase-number">Phase 3</span>
                                <h4>Services - Toast and BlockUI</h4>
                                <span class="effort">Esfuerzo: 3-5 days</span>
                            </div>
                            <div class="phase-content">
                                <ul>
                                    <li>🔧 Implement wrapper service for <code>MatSnackBar</code> compatible con API
                                        de
                                        toast</li>
                                    <li>🔧 Create BlockUI service using Material Overlay CDK</li>
                                    <li>🔧 Migrate all calls to new services</li>
                                    <li>✅ Testing of notifications and loading states</li>
                                </ul>
                                <p><strong>Benefit:</strong> Reducción de ~30KB, More modern and maintainable API</p>
                            </div>
                        </div>

                        <div class="phase">
                            <div class="phase-header phase-4">
                                <span class="phase-number">Phase 4</span>
                                <h4>Galleria - Evaluation</h4>
                                <span class="effort">Esfuerzo: 1 semana (si se migra)</span>
                            </div>
                            <div class="phase-content">
                                <ul>
                                    <li>📊 Evaluate current Galleria usage in the application</li>
                                    <li>🔍 Investigate alternatives: ngx-gallery, photoswipe, custom solution</li>
                                    <li>💡 Decisión: Migrar vs Mantener PrimeNG only para este componente</li>
                                </ul>
                                <p><strong>Consideration:</strong> Si only se usa en 1-2 lugares, puede justificarse
                                    mantener
                                    PrimeNG</p>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 48px;">Executive Summary</h3>

                    <div class="executive-summary">
                        <div class="summary-card">
                            <h4>🎯 Complete Migration Feasibility</h4>
                            <div class="verdict success">
                                <strong>FACTIBLE</strong> with moderate effort
                            </div>
                            <p>Es posible migrar ~90% de los componentes PrimeNG a Material sin pérdida de
                                funcionalidad. El 10%
                                restante (p-table para grids complejos, posiblemente Galleria) puede justificar mantener
                                PrimeNG.</p>
                        </div>

                        <div class="summary-card">
                            <h4>📊 Estimated Impact</h4>
                            <ul>
                                <li><strong>Bundle Size:</strong> Reducción de ~180-200KB (si se elimina PrimeNG
                                    completamente)
                                </li>
                                <li><strong>Total Effort:</strong> 3-4 weeks de desarrollo + 1 semana de testing
                                </li>
                                <li><strong>Risk:</strong> Medio (requiere testing exhaustivo de forms)</li>
                                <li><strong>Benefit:</strong> Alto (consistencia, mantenibilidad, mejor UX)</li>
                            </ul>
                        </div>

                        <div class="summary-card">
                            <h4>💡 Final Recommendation</h4>
                            <div class="recommendation">
                                <p><strong>Strategic Hybrid Approach:</strong></p>
                                <ol>
                                    <li>Migrar <strong>todos</strong> los Simple Components (buttons, checkboxes,
                                        radios) →
                                        Material</li>
                                    <li>Migrar <strong>todos</strong> los forms (inputs, selects) → Material</li>
                                    <li>Migrar servicios (toast, blockUI) → Material/Custom</li>
                                    <li><strong>Mantener</strong> PrimeNG <strong>only</strong> for:
                                        <ul>
                                            <li>p-table en Results Details (complex grid with advanced features)</li>
                                            <li>p-table en Savanna Config (if it requires advanced features)</li>
                                            <li>Galleria (evaluate if frequently used)</li>
                                        </ul>
                                    </li>
                                </ol>
                                <p><strong>Result:</strong> Reducción de dependencia de PrimeNG de ~80% a ~5%,
                                    manteniendo
                                    only componentes donde ofrece valor real.</p>
                            </div>
                        </div>
                    </div>

                </section>
            </div>
        </mat-tab>

        <!-- TAB 1: Colors & Typography -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">palette</mat-icon>
                Colors & Typography
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Color Palette</h2>
                    <p class="specs-note"><strong>Usage:</strong> Use these specific hex codes to maintain consistent
                        alert levels and brand identity across the application.</p>
                    <div class="color-grid">
                        <div class="swatch primary">
                            <span>Primary</span>
                            <span class="code">#1976d2</span>
                            <span class="usage">Buttons, Links, Headers</span>
                        </div>
                        <div class="swatch success">
                            <span>Success</span>
                            <span class="code">#5cb85c</span>
                            <span class="usage">Success messages, Active status</span>
                        </div>
                        <div class="swatch warn">
                            <span>Warn</span>
                            <span class="code">#f99157</span>
                            <span class="usage">Warnings, Pending status</span>
                        </div>
                        <div class="swatch error">
                            <span>Error</span>
                            <span class="code">#d31b1b</span>
                            <span class="usage">Errors, Delete actions</span>
                        </div>
                        <div class="swatch grey">
                            <span>Light Grey</span>
                            <span class="code">#8f9cb5</span>
                            <span class="usage">Secondary text, Disabled states</span>
                        </div>
                        <div class="swatch lighter-grey">
                            <span>Lighter Grey</span>
                            <span class="code">#b6bece</span>
                            <span class="usage">Borders, Dividers</span>
                        </div>
                    </div>
                </section>

                <section style="margin-top: 40px;">
                    <h2>Typography Scale</h2>
                    <p class="specs-note">
                        <strong>Font Family:</strong> <code>Roboto, 'Helvetica Neue', sans-serif</code><br>
                        <strong>Weights:</strong> Regular (400) for body text, Medium (500) for headers and buttons<br>
                        <strong>Letter Spacing:</strong> <code>normal</code> (Material components may override)
                    </p>
                    <div class="type-demo">
                        <div class="example-block">
                            <span class="size-display">24px</span>
                            <span style="font-size: 24px; font-weight: 500;">Page Title (H1)</span>
                            <span class="prop-detail">Usage: Main page headings (Experiments, Results, Manage
                                Assays)</span>
                        </div>
                        <div class="example-block">
                            <span class="size-display">20px</span>
                            <span style="font-size: 20px; font-weight: 500;">Section Header (H2, Dialog Title)</span>
                            <span class="prop-detail">Usage: Major section dividers, Dialog titles, Card headers</span>
                        </div>
                        <div class="example-block">
                            <span class="size-display">16px</span>
                            <span style="font-size: 16px; font-weight: 500;">Card Title (H3)</span>
                            <span class="prop-detail">Usage: Card headers, sub-sections, grouped content</span>
                        </div>
                        <div class="example-block">
                            <span class="size-display">14px</span>
                            <span style="font-size: 14px;"><strong>Standard Body (14px)</strong> - The default for most
                                UI elements</span>
                            <span class="prop-detail">Usage: Table content, <strong>Forms & Inputs</strong>, buttons,
                                paragraphs,
                                labels</span>
                        </div>
                        <div class="example-block">
                            <span class="size-display">12px</span>
                            <span style="font-size: 12px; color: #666;">Secondary Text (12px)</span>
                            <span class="prop-detail">Usage: Input hints, meta-data, secondary labels, timestamps</span>
                        </div>
                    </div>

                    <h2 style="margin-top: 48px;">Spacing & Layout System</h2>
                    <p class="specs-note"><strong>Consistency:</strong> Use the 4px base unit system for all spacing to
                        maintain visual rhythm and alignment.</p>

                    <h3>Spacing Scale</h3>
                    <div class="spacing-scale">
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 4px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>4px</strong>
                                <span>Minimal spacing, icon padding</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 8px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>8px</strong>
                                <span>Small gaps, chip padding</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 12px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>12px</strong>
                                <span>Form field gaps, list item padding</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 16px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>16px</strong>
                                <span>Standard spacing, card padding</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 24px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>24px</strong>
                                <span>Section spacing, dialog padding</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 32px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>32px</strong>
                                <span>Large gaps, page margins</span>
                            </div>
                        </div>
                        <div class="spacing-item">
                            <div class="spacing-visual" style="width: 48px; height: 40px; background: #1976d2;"></div>
                            <div class="spacing-info">
                                <strong>48px</strong>
                                <span>Major sections, hero spacing</span>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Responsive Breakpoints</h3>
                    <div class="breakpoints-grid">
                        <div class="breakpoint-card">
                            <mat-icon>phone_iphone</mat-icon>
                            <h4>Mobile</h4>
                            <div class="breakpoint-value">&lt; 600px</div>
                            <p>Single column layout, stacked components</p>
                        </div>
                        <div class="breakpoint-card">
                            <mat-icon>tablet</mat-icon>
                            <h4>Tablet</h4>
                            <div class="breakpoint-value">600px - 960px</div>
                            <p>2-column grid, condensed navigation</p>
                        </div>
                        <div class="breakpoint-card">
                            <mat-icon>laptop</mat-icon>
                            <h4>Desktop</h4>
                            <div class="breakpoint-value">960px - 1280px</div>
                            <p>Full layout, expanded navigation</p>
                        </div>
                        <div class="breakpoint-card">
                            <mat-icon>desktop_windows</mat-icon>
                            <h4>Large Desktop</h4>
                            <div class="breakpoint-value">&gt; 1280px</div>
                            <p>Wide layout, maximum content width</p>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Container Widths</h3>
                    <div class="container-examples">
                        <div class="container-item">
                            <div class="container-label">Full Width</div>
                            <div class="container-bar" style="width: 100%; background: #e3f2fd;">100%</div>
                            <span class="container-usage">Tables, data grids, dashboards</span>
                        </div>
                        <div class="container-item">
                            <div class="container-label">Content Width</div>
                            <div class="container-bar" style="width: 80%; background: #e8f5e9;">1200px max</div>
                            <span class="container-usage">Forms, detail pages, content areas</span>
                        </div>
                        <div class="container-item">
                            <div class="container-label">Narrow Width</div>
                            <div class="container-bar" style="width: 60%; background: #fff3e0;">800px max</div>
                            <span class="container-usage">Dialogs, modals, focused content</span>
                        </div>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 2: Buttons -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">smart_button</mat-icon>
                Buttons
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Buttons</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material (Standard)</span>
                            <span class="source-badge prime">PrimeNG (Legacy)</span>
                        </div>
                    </div>

                    <div class="warning-block">
                        <strong>⚠️ Critical Inconsistency:</strong> Experiment List toolbar mixes both
                        <code>mat-icon-button</code> and <code>pButton</code> in the same component, creating visual
                        inconsistency and maintenance burden.
                    </div>

                    <div class="warning-block" style="background-color: #fff9e6; border-left-color: #ffa726;">
                        <strong>💡 Recommendation:</strong> Migrate all <code>pButton</code> instances to Material
                        buttons. PrimeNG buttons offer no advantage over Material buttons and add unnecessary library
                        dependency.
                    </div>

                    <div class="location-list">
                        <strong>Material Buttons Used In:</strong>
                        <ul>
                            <li><strong>Experiment List:</strong> Create & Icon buttons (toolbar) ✅
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">experiment-list.component.html</span>
                                </div>
                            </li>
                            <li><strong>IC Experiments:</strong> All action buttons
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">ic-experiments.component.html</span>
                                </div>
                            </li>
                            <li><strong>Detail List:</strong> Action buttons
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">detail-list.component.html</span>
                                </div>
                            </li>
                            <li><strong>All Dialogs:</strong> Footer action buttons (Cancel, Confirm, Save)
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">Multiple dialog components</span>
                                    <span class="file-count">10+</span>
                                </div>
                            </li>
                            <li><strong>Manage Assays:</strong> Add/Edit buttons
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">manage-assays.component.html</span>
                                </div>
                            </li>
                            <li><strong>Bundle List:</strong> Create bundle button
                                <div class="file-reference">
                                    <mat-icon>description</mat-icon>
                                    <span class="file-path">bundle-list.component.html</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div class="location-list" style="background-color: #feeae6; border-left-color: #c62828;">
                        <strong>PrimeNG Buttons Found In (Should Migrate):</strong>
                        <ul>
                            <li><strong>IC Experiments:</strong> Some toolbar buttons
                                <div class="file-reference">
                                    <mat-icon>warning</mat-icon>
                                    <span class="file-path">ic-experiments.component.html</span>
                                </div>
                            </li>
                            <li><strong>Detail List:</strong> Mixed with Material buttons
                                <div class="file-reference">
                                    <mat-icon>warning</mat-icon>
                                    <span class="file-path">detail-list.component.html</span>
                                </div>
                            </li>
                            <li><strong>Column Filter:</strong> Filter action buttons
                                <div class="file-reference">
                                    <mat-icon>warning</mat-icon>
                                    <span class="file-path">column-filter.component.html</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Font Size:</strong> <code>14px</code> | <strong>Weight:</strong> <code>500</code> |
                        <strong>Height:</strong> 36px (standard), 40px (raised) | <strong>Padding:</strong> 0 16px
                    </p>

                    <div class="code-snippet-box">
                        <div class="snippet-header">Standard Implementation (Material)</div>
                        <pre><code ngNonBindable>&lt;!-- Primary action --&gt;
&lt;button mat-raised-button color="primary"&gt;Primary Action&lt;/button&gt;

&lt;!-- Secondary action --&gt;
&lt;button mat-stroked-button color="primary"&gt;Secondary Action&lt;/button&gt;

&lt;!-- Icon button --&gt;
&lt;button mat-icon-button&gt;&lt;mat-icon&gt;settings&lt;/mat-icon&gt;&lt;/button&gt;

&lt;!-- FAB (Floating Action Button) --&gt;
&lt;button mat-fab color="primary"&gt;&lt;mat-icon&gt;add&lt;/mat-icon&gt;&lt;/button&gt;</code></pre>
                    </div>

                    <h3 class="subsection-title">Button Variants</h3>
                    <div class="demo-row">
                        <button mat-raised-button color="primary">Raised Primary</button>
                        <button mat-raised-button color="accent">Raised Accent</button>
                        <button mat-raised-button color="warn">Raised Warn</button>
                        <button mat-stroked-button color="primary">Stroked</button>
                        <button mat-flat-button color="primary">Flat</button>
                        <button mat-icon-button><mat-icon>settings</mat-icon></button>
                        <button mat-fab color="primary" style="transform: scale(0.7);"><mat-icon>add</mat-icon></button>
                    </div>

                    <h3 class="subsection-title">PrimeNG Buttons (Legacy - Avoid)</h3>
                    <div class="demo-row">
                        <button pButton type="button" label="PrimeNG Raised"
                            class="p-button-raised p-button-primary"></button>
                        <button pButton type="button" label="PrimeNG Secondary"
                            class="p-button-raised p-button-secondary"></button>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 3: Forms & Inputs -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">input</mat-icon>
                Forms & Inputs
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Form Inputs & Text Fields</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material (Standard)</span>
                            <span class="source-badge prime">PrimeNG (Widespread Legacy)</span>
                        </div>
                    </div>

                    <div class="warning-block">
                        <strong>⚠️ Critical Hybrid Pattern:</strong> Experiment Dialog uses <code>mat-dialog</code>
                        container but <code>pInputText</code> and <code>p-select</code> form controls inside, creating
                        severe styling inconsistencies and maintenance issues.
                    </div>

                    <div class="warning-block" style="background-color: #ffebee; border-left-color: #d32f2f;">
                        <strong>🔴 Major Technical Debt:</strong> PrimeNG inputs are heavily used across 15+ components.
                        This creates:
                        <ul style="margin: 8px 0 0 20px;">
                            <li>Inconsistent form validation UX</li>
                            <li>Different keyboard navigation patterns</li>
                            <li>Duplicate styling maintenance</li>
                            <li>Larger bundle size</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Material Inputs Used In:</strong>
                        <ul>
                            <li><strong>Advanced Bundle Edit:</strong> All form fields</li>
                            <li><strong>Manage Assays:</strong> Assay name, description</li>
                            <li><strong>Bundle List:</strong> Search field ✅</li>
                            <li><strong>Sample List:</strong> Search field ✅</li>
                            <li><strong>Experiment List:</strong> Search field ✅</li>
                        </ul>
                    </div>

                    <div class="location-list" style="background-color: #feeae6; border-left-color: #c62828;">
                        <strong>PrimeNG Inputs Found In (High Priority Migration):</strong>
                        <ul>
                            <li><strong>Experiment Dialog:</strong> All text inputs (Name, Description, etc.)</li>
                            <li><strong>IC Experiment Dialog:</strong> All form fields</li>
                            <li><strong>Results Details:</strong> Filter and search fields</li>
                            <li><strong>Logistics:</strong> All input fields</li>
                            <li><strong>Condition List:</strong> Search inputs</li>
                            <li><strong>File Upload:</strong> Text inputs</li>
                            <li><strong>Device/Instrument Error Dialogs:</strong> All inputs</li>
                            <li><strong>Savanna Config:</strong> Configuration inputs</li>
                            <li><strong>Choose Experiment:</strong> Search field</li>
                            <li><strong>Select External Control:</strong> Input fields</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Style:</strong> <code>appearance="outline"</code> (Material standard)<br>
                        <strong>Text Size:</strong> <code>14px</code><br>
                        <strong>Background:</strong> Inherited/Transparent (Do NOT override with white background)<br>
                        <strong>Label:</strong> Floating (moves up when focused/filled)<br>
                        <strong>Height:</strong> 56px (with label)
                    </p>

                    <div class="code-snippet-box">
                        <div class="snippet-header">Standard Implementation (Material)</div>
                        <pre><code ngNonBindable>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Label&lt;/mat-label&gt;
  &lt;input matInput placeholder="Placeholder"&gt;
  &lt;mat-hint&gt;Helper text&lt;/mat-hint&gt;
  &lt;mat-error *ngIf="hasError"&gt;Error message&lt;/mat-error&gt;
&lt;/mat-form-field&gt;

&lt;!-- With icon --&gt;
&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Search&lt;/mat-label&gt;
  &lt;mat-icon matPrefix&gt;search&lt;/mat-icon&gt;
  &lt;input matInput&gt;
&lt;/mat-form-field&gt;</code></pre>
                    </div>

                    <h3 class="subsection-title">Material Examples (Standard)</h3>
                    <div class="demo-row">
                        <mat-form-field appearance="outline">
                            <mat-label>Standard Input</mat-label>
                            <input matInput placeholder="Ex. Unit 42">
                            <mat-hint>14px Text</mat-hint>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>With Icon</mat-label>
                            <mat-icon matPrefix>search</mat-icon>
                            <input matInput>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Date Picker</mat-label>
                            <input matInput [matDatepicker]="picker">
                            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                            <mat-datepicker #picker></mat-datepicker>
                        </mat-form-field>
                    </div>

                    <h3 class="subsection-title">Material Replacements</h3>
                    <div class="demo-row">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Input</mat-label>
                            <input type="text" matInput placeholder="Material Input" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Dropdown</mat-label>
                            <mat-select>
                                <mat-option value="A">Option A</mat-option>
                                <mat-option value="B">Option B</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 4: Selection Controls -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">check_box</mat-icon>
                Selection Controls
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Checkboxes, Radio Buttons & Toggles</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material (Standard)</span>
                            <span class="source-badge prime">PrimeNG (Legacy)</span>
                        </div>
                    </div>

                    <div class="warning-block" style="background-color: #fff9e6; border-left-color: #ffa726;">
                        <strong>💡 Mixed Usage:</strong> Some forms use Material checkboxes while others use PrimeNG.
                        This creates inconsistent interaction patterns and visual styles.
                    </div>

                    <div class="location-list">
                        <strong>Material Checkboxes/Radios Used In:</strong>
                        <ul>
                            <li><strong>Results Details:</strong> Instrument Error, Unevaluable flags</li>
                            <li><strong>Standard Forms:</strong> Boolean selections</li>
                            <li><strong>Filter Panels:</strong> Multi-select filters</li>
                        </ul>
                    </div>

                    <div class="location-list" style="background-color: #feeae6; border-left-color: #c62828;">
                        <strong>PrimeNG Checkboxes/Radios Found In:</strong>
                        <ul>
                            <li><strong>Experiment Dialog:</strong> Experiment Type selection (radio buttons)</li>
                            <li><strong>Legacy Modules:</strong> Isolated usage in older components</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Material Toggles Used In:</strong>
                        <ul>
                            <li><strong>List Pages:</strong> Show/Hide archived items</li>
                            <li><strong>Filter Panels:</strong> Enable/disable specific filters</li>
                            <li><strong>Settings:</strong> Feature toggles</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Font Size:</strong> <code>14px</code> label | <strong>Color:</strong> Primary theme |
                        <strong>Touch Target:</strong> 40x40px minimum
                    </p>

                    <div class="code-snippet-box">
                        <div class="snippet-header">Standard Implementation</div>
                        <pre><code ngNonBindable>&lt;!-- Checkbox --&gt;
&lt;mat-checkbox color="primary"&gt;Checkbox Label&lt;/mat-checkbox&gt;

&lt;!-- Radio Group --&gt;
&lt;mat-radio-group&gt;
  &lt;mat-radio-button value="1"&gt;Option 1&lt;/mat-radio-button&gt;
  &lt;mat-radio-button value="2"&gt;Option 2&lt;/mat-radio-button&gt;
&lt;/mat-radio-group&gt;

&lt;!-- Toggle --&gt;
&lt;mat-slide-toggle color="primary"&gt;Toggle Label&lt;/mat-slide-toggle&gt;</code></pre>
                    </div>

                    <div class="demo-row">
                        <mat-checkbox color="primary" [checked]="true">Material Checkbox</mat-checkbox>
                        <mat-slide-toggle color="primary">Material Toggle</mat-slide-toggle>
                        <mat-checkbox color="primary">Material Checkbox (Replacement)</mat-checkbox>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 5: Tables & Grids -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">table_chart</mat-icon>
                Tables & Grids
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Tables & Data Grids</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material (Standard)</span>
                            <span class="source-badge prime">PrimeNG (Complex Grids Only)</span>
                        </div>
                    </div>

                    <div class="warning-block" style="background-color: #fff9e6; border-left-color: #ffa726;">
                        <strong>💡 Recommendation:</strong> Use <code>mat-table</code> for standard lists. Reserve
                        <code>p-table</code> only for spreadsheet-like views requiring advanced features (column
                        reordering, freezing, complex filters).
                    </div>

                    <div class="location-list">
                        <strong>Material Table Used In:</strong>
                        <ul>
                            <li><strong>Experiment List:</strong> Main experiments table</li>
                            <li><strong>IC Experiments:</strong> Image capture experiments list</li>
                            <li><strong>Sample List:</strong> Samples data table</li>
                            <li><strong>Manage Assays:</strong> Assays management table</li>
                            <li><strong>Audit Dialog:</strong> Audit history table</li>
                            <li><strong>Bundle List:</strong> Results bundles table</li>
                            <li><strong>Condition List:</strong> Conditions table</li>
                            <li><strong>Detail List:</strong> Experiment details table</li>
                            <li><strong>Logistics List:</strong> Logistics data table</li>
                            <li><strong>File Upload:</strong> Uploaded files table</li>
                            <li><strong>IC Sequence:</strong> Sequence data table</li>
                            <li><strong>IC Images Dialog:</strong> Images list table</li>
                            <li><strong>IC Adjust Legend:</strong> Legend items table</li>
                            <li><strong>Choose Experiment:</strong> Experiment selection table</li>
                            <li><strong>Results Summary:</strong> Summary data table</li>
                            <li><strong>Automated Reports:</strong> Reports table</li>
                            <li><strong>Select External Control:</strong> Control selection table</li>
                            <li><strong>Experiment List for Bundle:</strong> Bundle experiments table</li>
                        </ul>
                    </div>

                    <div class="location-list" style="background-color: #feeae6; border-left-color: #c62828;">
                        <strong>PrimeNG Table Found In (Justified - Complex Features):</strong>
                        <ul>
                            <li><strong>Results Details:</strong> Complex grid with column freezing, reordering,
                                filtering</li>
                            <li><strong>Fix Duplicate Results:</strong> Advanced data manipulation grid</li>
                            <li><strong>Savanna Config:</strong> Configuration management grid</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Header:</strong> <code>14px</code>, weight <code>500</code>, background
                        <code>#f5f5f5</code><br>
                        <strong>Body:</strong> <code>14px</code>, vertical padding <code>10-12px</code><br>
                        <strong>Pagination:</strong> <code>mat-paginator</code> with options [5, 10, 25, 100]
                    </p>

                    <div class="code-snippet-box">
                        <div class="snippet-header">Standard Implementation (Material)</div>
                        <pre><code ngNonBindable>&lt;table mat-table [dataSource]="dataSource"&gt;
  &lt;ng-container matColumnDef="name"&gt;
    &lt;th mat-header-cell *matHeaderCellDef&gt; Name &lt;/th&gt;
    &lt;td mat-cell *matCellDef="let element"&gt; {{element.name}} &lt;/td&gt;
  &lt;/ng-container&gt;
  &lt;tr mat-header-row *matHeaderRowDef="displayedColumns"&gt;&lt;/tr&gt;
  &lt;tr mat-row *matRowDef="let row; columns: displayedColumns;"&gt;&lt;/tr&gt;
&lt;/table&gt;
&lt;mat-paginator [pageSizeOptions]="[5, 10, 25, 100]"&gt;&lt;/mat-paginator&gt;</code></pre>
                    </div>

                    <div class="table-demo">
                        <div class="demo-header-simple">Material Table Example</div>
                        <table mat-table [dataSource]="demoTableData" style="width: 100%">
                            <ng-container matColumnDef="name">
                                <th mat-header-cell *matHeaderCellDef> Name </th>
                                <td mat-cell *matCellDef="let element"> <strong>{{element.name}}</strong> </td>
                            </ng-container>

                            <ng-container matColumnDef="type">
                                <th mat-header-cell *matHeaderCellDef> Type </th>
                                <td mat-cell *matCellDef="let element"> {{element.type}} </td>
                            </ng-container>

                            <ng-container matColumnDef="date">
                                <th mat-header-cell *matHeaderCellDef> Date </th>
                                <td mat-cell *matCellDef="let element"> {{element.date}} </td>
                            </ng-container>

                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef> Status </th>
                                <td mat-cell *matCellDef="let element">
                                    <span class="status-badge" [ngClass]="element.status.toLowerCase()">
                                        {{element.status}}
                                    </span>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="actions">
                                <th mat-header-cell *matHeaderCellDef> </th>
                                <td mat-cell *matCellDef="let element" style="text-align: right;">
                                    <button mat-icon-button color="primary"><mat-icon>edit</mat-icon></button>
                                </td>
                            </ng-container>

                            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
                        </table>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 6: Dialogs & Modals -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">web_asset</mat-icon>
                Dialogs & Modals
                <mat-icon class="warning-indicator">warning</mat-icon>
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Dialogs & Modals</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material Only</span>
                        </div>
                    </div>

                    <div class="warning-block">
                        <strong>⚠️ Hybrid Content Warning:</strong> While all dialogs use <code>MatDialog</code> as the
                        container, some (like Experiment Dialog) contain PrimeNG form controls inside. This creates
                        styling inconsistencies.
                    </div>

                    <div class="location-list">
                        <strong>Standard Dialogs:</strong>
                        <ul>
                            <li><strong>Confirm Dialog:</strong> Generic confirmation dialogs</li>
                            <li><strong>Audit Dialog:</strong> Audit history viewer</li>
                            <li><strong>Notes Dialog:</strong> Notes editor</li>
                            <li><strong>Changes Dialog:</strong> Change tracking dialog</li>
                            <li><strong>Release Notes:</strong> Application release notes</li>
                            <li><strong>Contact & Support:</strong> Support information dialog</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Form Dialogs:</strong>
                        <ul>
                            <li><strong>Experiment Dialog:</strong> Create/edit experiments (⚠️ uses PrimeNG inputs)
                            </li>
                            <li><strong>IC Experiment Dialog:</strong> Image capture experiments (⚠️ uses PrimeNG
                                inputs)</li>
                            <li><strong>Assay Dialog:</strong> Manage assays</li>
                            <li><strong>Barcode Dialog:</strong> Barcode management</li>
                            <li><strong>Advanced Bundle Dialog:</strong> Bundle configuration</li>
                            <li><strong>Instrument Error Dialog:</strong> Error reporting</li>
                            <li><strong>Device Target Error Dialog:</strong> Device error details</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Display Dialogs:</strong>
                        <ul>
                            <li><strong>IC Images Dialog:</strong> Image viewer</li>
                            <li><strong>Sample Summary:</strong> Sample details viewer</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Header:</strong> <code>mat-toolbar color="primary"</code>, 48-64px height, 16-20px
                        font<br>
                        <strong>Content:</strong> <code>mat-dialog-content</code>, 24px padding<br>
                        <strong>Actions:</strong> <code>mat-dialog-actions</code>, right-aligned, 12-16px padding
                    </p>

                    <div class="code-snippet-box">
                        <div class="snippet-header">Standard Implementation</div>
                        <pre><code ngNonBindable>&lt;mat-toolbar color="primary"&gt;Dialog Title&lt;/mat-toolbar&gt;
&lt;div mat-dialog-content&gt;
  &lt;!-- Content here --&gt;
&lt;/div&gt;
&lt;div mat-dialog-actions align="end"&gt;
  &lt;button mat-stroked-button mat-dialog-close&gt;Cancel&lt;/button&gt;
  &lt;button mat-raised-button color="primary"&gt;Confirm&lt;/button&gt;
&lt;/div&gt;</code></pre>
                    </div>

                    <div class="dialog-demo">
                        <mat-toolbar color="primary" style="height: 48px; font-size: 16px;">
                            <span>Confirm Action</span>
                        </mat-toolbar>
                        <div class="dialog-content">
                            Are you sure you would like to proceed with this action?
                        </div>
                        <div class="dialog-actions">
                            <button mat-stroked-button color="primary">Cancel</button>
                            <button mat-raised-button color="primary">Confirm</button>
                        </div>
                    </div>
                </section>
            </div>
        </mat-tab>

        <!-- TAB 7: Feedback & Progress -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">hourglass_empty</mat-icon>
                Feedback & Progress
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Progress Indicators & Feedback</h2>

                    <div class="component-header">
                        <h3>Framework Usage</h3>
                        <div class="badge-row">
                            <span class="source-badge mat">Material (Standard)</span>
                        </div>
                    </div>

                    <h3>Progress Indicators</h3>
                    <div class="location-list">
                        <strong>Spinner Used In:</strong>
                        <ul>
                            <li><strong>Global Loader:</strong> Full-page loading overlay</li>
                            <li><strong>Dialog Loading:</strong> Dialog content loading states</li>
                            <li><strong>Inline Loading:</strong> Component-level loading indicators</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Progress Bar Used In:</strong>
                        <ul>
                            <li><strong>File Uploads:</strong> Upload progress indication</li>
                            <li><strong>Data Processing:</strong> Long-running operation progress</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Spinner:</strong> Diameter <code>40px</code> (standard), <code>60px</code> (large)<br>
                        <strong>Progress Bar:</strong> Height <code>4px</code>, modes: indeterminate/determinate
                    </p>

                    <div class="demo-row">
                        <div class="progress-item">
                            <mat-spinner diameter="40"></mat-spinner>
                            <p class="demo-label">Standard Spinner (40px)</p>
                        </div>
                        <div class="progress-item" style="flex: 1; max-width: 300px;">
                            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                            <p class="demo-label">Indeterminate Progress Bar</p>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Badges & Status Indicators</h3>
                    <div class="location-list">
                        <strong>Material Badges Used In:</strong>
                        <ul>
                            <li><strong>Notifications:</strong> Unread count indicators</li>
                            <li><strong>Alerts:</strong> Alert count badges</li>
                        </ul>
                    </div>

                    <div class="location-list">
                        <strong>Custom Status Badges Used In:</strong>
                        <ul>
                            <li><strong>Experiment Lists:</strong> Status indicators (Active, Error, Pending, Archived)
                            </li>
                            <li><strong>Results:</strong> Result status badges</li>
                            <li><strong>Bundles:</strong> Bundle status indicators</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Font Size:</strong> <code>12px</code> | <strong>Padding:</strong> <code>4-8px</code> |
                        <strong>Border Radius:</strong> <code>4px</code>
                    </p>

                    <div class="demo-row">
                        <button mat-icon-button matBadge="4" matBadgeColor="warn">
                            <mat-icon>notifications</mat-icon>
                        </button>
                        <span class="status-badge active">Active</span>
                        <span class="status-badge error">Error</span>
                        <span class="status-badge pending">Pending</span>
                        <span class="status-badge archived">Archived</span>
                    </div>

                    <h2 style="margin-top: 48px;">Error Handling Patterns</h2>
                    <p class="specs-note"><strong>Consistency:</strong> Use consistent error messaging and visual
                        feedback across all forms and operations.</p>

                    <h3>Form Validation Errors</h3>
                    <div class="error-patterns-grid">
                        <div class="error-pattern-card">
                            <h4><mat-icon>error</mat-icon> Inline Field Errors</h4>
                            <p><strong>When:</strong> User enters invalid data in a form field</p>
                            <p><strong>How:</strong> Display error message below field with red color</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Material Form Error</div>
                                <pre><code ngNonBindable>&lt;mat-form-field&gt;
  &lt;input matInput placeholder="Email" [formControl]="emailControl"&gt;
  &lt;mat-error *ngIf="emailControl.hasError('required')"&gt;
    Email is required
  &lt;/mat-error&gt;
  &lt;mat-error *ngIf="emailControl.hasError('email')"&gt;
    Please enter a valid email
  &lt;/mat-error&gt;
&lt;/mat-form-field&gt;</code></pre>
                            </div>
                        </div>

                        <div class="error-pattern-card">
                            <h4><mat-icon>warning</mat-icon> Form-Level Errors</h4>
                            <p><strong>When:</strong> Multiple fields have errors or form submission fails</p>
                            <p><strong>How:</strong> Display error summary at top of form</p>
                            <div class="error-example warning-block">
                                <strong>⚠️ Please correct the following errors:</strong>
                                <ul>
                                    <li>Email is required</li>
                                    <li>Password must be at least 8 characters</li>
                                </ul>
                            </div>
                        </div>

                        <div class="error-pattern-card">
                            <h4><mat-icon>info</mat-icon> Toast Notifications</h4>
                            <p><strong>When:</strong> API errors, save failures, network issues</p>
                            <p><strong>How:</strong> Use MatSnackBar for temporary error messages</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Error Toast Example</div>
                                <pre><code ngNonBindable>this.snackBar.open(
  'Failed to save experiment',
  'Retry',
  &amp;#123;
    duration: 5000,
    panelClass: ['error-snackbar']
  &amp;#125;
);</code></pre>
                            </div>
                        </div>

                        <div class="error-pattern-card">
                            <h4><mat-icon>cancel</mat-icon> Dialog Errors</h4>
                            <p><strong>When:</strong> Critical errors requiring user acknowledgment</p>
                            <p><strong>How:</strong> Use MatDialog with error styling</p>
                            <div class="error-example" style="background: #ffebee; border-left-color: #d31b1b;">
                                <strong>❌ Operation Failed</strong>
                                <p>Unable to delete experiment. It is currently in use by an active bundle.</p>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Error Message Guidelines</h3>
                    <div class="guidelines-list">
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Be specific and actionable
                                <code>"Email must contain @ symbol"</code>
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Be vague or technical
                                <code>"Invalid input"</code> or <code>"Error code: 422"</code>
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Suggest solutions
                                <code>"File size exceeds 10MB. Please compress or choose a smaller file."</code>
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Blame the user
                                <code>"You entered the wrong format"</code>
                            </div>
                        </div>
                    </div>

                    <h2 style="margin-top: 48px;">Loading States Best Practices</h2>
                    <p class="specs-note"><strong>User Experience:</strong> Always provide visual feedback during
                        asynchronous operations to prevent user confusion.</p>

                    <h3>When to Use Each Loading Pattern</h3>
                    <div class="loading-patterns-grid">
                        <div class="loading-pattern-card">
                            <div class="pattern-header spinner">
                                <mat-spinner diameter="32"></mat-spinner>
                                <h4>Full-Page Spinner</h4>
                            </div>
                            <div class="pattern-content">
                                <p><strong>Use when:</strong></p>
                                <ul>
                                    <li>Initial page load</li>
                                    <li>Navigating between routes</li>
                                    <li>Loading critical data</li>
                                </ul>
                                <p><strong>Duration:</strong> &lt; 3 seconds ideal</p>
                                <p class="warning-text">⚠️ Avoid for operations &gt; 5 seconds</p>
                            </div>
                        </div>

                        <div class="loading-pattern-card">
                            <div class="pattern-header progress">
                                <mat-progress-bar mode="indeterminate" style="width: 100%;"></mat-progress-bar>
                                <h4>Progress Bar</h4>
                            </div>
                            <div class="pattern-content">
                                <p><strong>Use when:</strong></p>
                                <ul>
                                    <li>File uploads</li>
                                    <li>Data processing with known progress</li>
                                    <li>Multi-step operations</li>
                                </ul>
                                <p><strong>Mode:</strong> Determinate (with %) or Indeterminate</p>
                            </div>
                        </div>

                        <div class="loading-pattern-card">
                            <div class="pattern-header skeleton">
                                <div class="skeleton-example">
                                    <div class="skeleton-line" style="width: 80%;"></div>
                                    <div class="skeleton-line" style="width: 60%;"></div>
                                    <div class="skeleton-line" style="width: 90%;"></div>
                                </div>
                                <h4>Skeleton Screens</h4>
                            </div>
                            <div class="pattern-content">
                                <p><strong>Use when:</strong></p>
                                <ul>
                                    <li>Loading lists or tables</li>
                                    <li>Loading card-based layouts</li>
                                    <li>Content-heavy pages</li>
                                </ul>
                                <p><strong>Benefit:</strong> Perceived faster load time</p>
                                <p class="success-text">✅ Best UX for content loading</p>
                            </div>
                        </div>

                        <div class="loading-pattern-card">
                            <div class="pattern-header inline">
                                <mat-spinner diameter="20"></mat-spinner>
                                <h4>Inline Spinners</h4>
                            </div>
                            <div class="pattern-content">
                                <p><strong>Use when:</strong></p>
                                <ul>
                                    <li>Button actions (Save, Delete)</li>
                                    <li>Loading specific components</li>
                                    <li>Refreshing data sections</li>
                                </ul>
                                <p><strong>Size:</strong> 20-24px diameter</p>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Loading Button States</h3>
                    <div class="button-loading-examples">
                        <div class="button-state-example">
                            <h4>Normal State</h4>
                            <button mat-raised-button color="primary">Save Experiment</button>
                        </div>
                        <div class="button-state-example">
                            <h4>Loading State</h4>
                            <button mat-raised-button color="primary" disabled>
                                <mat-spinner diameter="20"
                                    style="display: inline-block; margin-right: 8px;"></mat-spinner>
                                Saving...
                            </button>
                        </div>
                        <div class="button-state-example">
                            <h4>Success State</h4>
                            <button mat-raised-button color="accent">
                                <mat-icon>check</mat-icon>
                                Saved
                            </button>
                        </div>
                    </div>

                    <div class="code-snippet-box" style="margin-top: 24px;">
                        <div class="snippet-header">Loading Button Implementation</div>
                        <pre><code ngNonBindable>&lt;button mat-raised-button 
        color="primary"
        [disabled]="isLoading"
        (click)="saveExperiment()"&gt;
  &lt;mat-spinner *ngIf="isLoading" 
               diameter="20" 
               style="display: inline-block; margin-right: 8px;"&gt;
  &lt;/mat-spinner&gt;
  &amp;#123;&amp;#123; isLoading ? 'Saving...' : 'Save Experiment' &amp;#125;&amp;#125;
&lt;/button&gt;</code></pre>
                    </div>

                    <h3 style="margin-top: 32px;">Loading State Guidelines</h3>
                    <div class="guidelines-list">
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Show loading state immediately when action starts
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Disable interactive elements during loading
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Provide progress indication for operations &gt; 2 seconds
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Use multiple loading indicators simultaneously
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Show loading state for &lt; 300ms operations (causes flicker)
                            </div>
                        </div>
                    </div>

                </section>
            </div>
        </mat-tab>

        <!-- TAB 8: Navigation & Layout -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">view_quilt</mat-icon>
                Navigation & Layout
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Navigation & Layout Components</h2>

                    <h3>Toolbars & Headers</h3>
                    <div class="location-list">
                        <strong>Main Toolbar:</strong>
                        <ul>
                            <li><strong>Application Header:</strong> Global navigation toolbar</li>
                            <li><strong>Dialog Headers:</strong> All dialog title bars</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Height:</strong> <code>64px</code> (main), <code>48px</code> (dialogs)<br>
                        <strong>Font Size:</strong> <code>20px</code> (main), <code>16px</code> (dialogs)<br>
                        <strong>Color:</strong> <code>color="primary"</code>
                    </p>

                    <div class="demo-row">
                        <mat-toolbar color="primary" style="width: 100%; height: 48px;">
                            <span>Example Toolbar</span>
                            <span style="flex: 1"></span>
                            <button mat-icon-button><mat-icon>menu</mat-icon></button>
                        </mat-toolbar>
                    </div>

                    <h3 style="margin-top: 32px;">Icons</h3>
                    <div class="location-list">
                        <strong>Material Icons Used Throughout:</strong>
                        <ul>
                            <li><strong>Buttons:</strong> Action icons (add, edit, delete, settings)</li>
                            <li><strong>Navigation:</strong> Menu icons, breadcrumbs</li>
                            <li><strong>Status:</strong> Status indicators (check, warning, error)</li>
                            <li><strong>Actions:</strong> Common actions (search, filter, download, upload)</li>
                        </ul>
                    </div>

                    <p class="specs-note">
                        <strong>Default Size:</strong> <code>24px</code> | <strong>Usage:</strong>
                        <code>&lt;mat-icon&gt;icon_name&lt;/mat-icon&gt;</code>
                    </p>

                    <div class="demo-row">
                        <mat-icon>home</mat-icon>
                        <mat-icon>settings</mat-icon>
                        <mat-icon>search</mat-icon>
                        <mat-icon>delete</mat-icon>
                        <mat-icon>edit</mat-icon>
                        <mat-icon>add</mat-icon>
                        <mat-icon>close</mat-icon>
                        <mat-icon>check</mat-icon>
                        <mat-icon>warning</mat-icon>
                        <mat-icon>error</mat-icon>
                    </div>

                    <h3 style="margin-top: 32px;">Filters & Search</h3>
                    <div class="warning-block">
                        <strong>⚠️ Technical Debt:</strong> Custom <code>app-column-filter</code> component duplicates
                        functionality available in both Material and PrimeNG. Consider migrating to standard library
                        components.
                    </div>

                    <div class="location-list">
                        <strong>Material Search Used In:</strong>
                        <ul>
                            <li><strong>Experiment List:</strong> Global search bar</li>
                            <li><strong>Sample List:</strong> Sample search</li>
                            <li><strong>Most List Pages:</strong> Standard search pattern</li>
                        </ul>
                    </div>

                    <div class="location-list" style="background-color: #feeae6; border-left-color: #c62828;">
                        <strong>Custom Column Filter Used In:</strong>
                        <ul>
                            <li><strong>Experiment List:</strong> Column-specific filtering</li>
                        </ul>
                    </div>

                    <div class="demo-row">
                        <mat-form-field appearance="outline" style="width: 300px;">
                            <mat-label>Search</mat-label>
                            <mat-icon matPrefix>search</mat-icon>
                            <input matInput placeholder="Search by name or ID...">
                        </mat-form-field>
                    </div>

                    <h2 style="margin-top: 48px;">Empty States & No Data Patterns</h2>
                    <p class="specs-note"><strong>User Experience:</strong> Empty states should be helpful and guide
                        users toward taking action, not just display "No data".</p>

                    <h3>Empty State Patterns</h3>
                    <div class="empty-states-grid">
                        <div class="empty-state-card">
                            <div class="empty-state-preview">
                                <mat-icon class="empty-icon">inbox</mat-icon>
                                <h4>No Experiments Yet</h4>
                                <p>Get started by creating your first experiment</p>
                                <button mat-raised-button color="primary">
                                    <mat-icon>add</mat-icon>
                                    Create Experiment
                                </button>
                            </div>
                            <div class="pattern-description">
                                <strong>First-Time Empty State</strong>
                                <p>When: User has never created any items</p>
                                <p>Include: Icon, message, primary action button</p>
                            </div>
                        </div>

                        <div class="empty-state-card">
                            <div class="empty-state-preview">
                                <mat-icon class="empty-icon search">search_off</mat-icon>
                                <h4>No Results Found</h4>
                                <p>Try adjusting your search or filters</p>
                                <button mat-stroked-button color="primary">Clear Filters</button>
                            </div>
                            <div class="pattern-description">
                                <strong>Search/Filter Empty State</strong>
                                <p>When: User's search/filter returns no results</p>
                                <p>Include: Suggestion to modify search, clear filters action</p>
                            </div>
                        </div>

                        <div class="empty-state-card">
                            <div class="empty-state-preview">
                                <mat-icon class="empty-icon error">error_outline</mat-icon>
                                <h4>Unable to Load Data</h4>
                                <p>There was a problem loading experiments</p>
                                <button mat-raised-button color="accent">
                                    <mat-icon>refresh</mat-icon>
                                    Retry
                                </button>
                            </div>
                            <div class="pattern-description">
                                <strong>Error Empty State</strong>
                                <p>When: Data failed to load due to error</p>
                                <p>Include: Error icon, helpful message, retry action</p>
                            </div>
                        </div>

                        <div class="empty-state-card">
                            <div class="empty-state-preview">
                                <mat-icon class="empty-icon">folder_open</mat-icon>
                                <h4>No Active Bundles</h4>
                                <p>All bundles have been archived or completed</p>
                            </div>
                            <div class="pattern-description">
                                <strong>Contextual Empty State</strong>
                                <p>When: Filtered view is legitimately empty</p>
                                <p>Include: Contextual message, optional action</p>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Empty State Code Example</h3>
                    <div class="code-snippet-box">
                        <div class="snippet-header">Empty State Component</div>
                        <pre><code ngNonBindable>&lt;div class="empty-state" *ngIf="experiments.length === 0"&gt;
  &lt;mat-icon class="empty-icon"&gt;inbox&lt;/mat-icon&gt;
  &lt;h3&gt;No Experiments Yet&lt;/h3&gt;
  &lt;p&gt;Get started by creating your first experiment&lt;/p&gt;
  &lt;button mat-raised-button 
          color="primary" 
          (click)="createExperiment()"&gt;
    &lt;mat-icon&gt;add&lt;/mat-icon&gt;
    Create Experiment
  &lt;/button&gt;
&lt;/div&gt;</code></pre>
                    </div>

                    <h3 style="margin-top: 32px;">Empty State Guidelines</h3>
                    <div class="guidelines-list">
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use friendly, conversational language
                                <code>"No experiments yet"</code> instead of <code>"0 records"</code>
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Provide a clear call-to-action when appropriate
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use relevant icons that match the context
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Just show "No data" without context or help
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Show empty states for temporary loading states
                            </div>
                        </div>
                    </div>

                    <h2 style="margin-top: 48px;">Data Visualization Guidelines</h2>
                    <p class="specs-note"><strong>Consistency:</strong> Use consistent chart types and colors for
                        similar data across the application.</p>

                    <h3>Chart Type Selection</h3>
                    <div class="chart-types-grid">
                        <div class="chart-type-card">
                            <div class="chart-icon-wrapper">
                                <mat-icon>show_chart</mat-icon>
                            </div>
                            <h4>Line Charts</h4>
                            <p><strong>Best for:</strong> Trends over time, continuous data</p>
                            <p><strong>Use when:</strong> Showing experiment results over time, performance metrics</p>
                            <div class="example-tag">Example: Temperature readings, growth curves</div>
                        </div>

                        <div class="chart-type-card">
                            <div class="chart-icon-wrapper">
                                <mat-icon>bar_chart</mat-icon>
                            </div>
                            <h4>Bar Charts</h4>
                            <p><strong>Best for:</strong> Comparing discrete categories</p>
                            <p><strong>Use when:</strong> Comparing experiment counts, status distribution</p>
                            <div class="example-tag">Example: Experiments by status, results by type</div>
                        </div>

                        <div class="chart-type-card">
                            <div class="chart-icon-wrapper">
                                <mat-icon>pie_chart</mat-icon>
                            </div>
                            <h4>Pie Charts</h4>
                            <p><strong>Best for:</strong> Part-to-whole relationships (max 5-6 slices)</p>
                            <p><strong>Use when:</strong> Showing percentage distribution</p>
                            <div class="example-tag warning">⚠️ Avoid for > 6 categories</div>
                        </div>

                        <div class="chart-type-card">
                            <div class="chart-icon-wrapper">
                                <mat-icon>table_chart</mat-icon>
                            </div>
                            <h4>Data Tables</h4>
                            <p><strong>Best for:</strong> Precise values, detailed comparisons</p>
                            <p><strong>Use when:</strong> Users need exact numbers, sorting, filtering</p>
                            <div class="example-tag success">✅ Best for detailed analysis</div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Chart Color Palette</h3>
                    <div class="chart-colors-grid">
                        <div class="color-swatch-row">
                            <div class="chart-color" style="background: #1976d2;">
                                <span class="color-label">Primary Blue</span>
                                <span class="color-code">#1976d2</span>
                            </div>
                            <div class="chart-color" style="background: #388e3c;">
                                <span class="color-label">Success Green</span>
                                <span class="color-code">#388e3c</span>
                            </div>
                            <div class="chart-color" style="background: #f57c00;">
                                <span class="color-label">Warning Orange</span>
                                <span class="color-code">#f57c00</span>
                            </div>
                            <div class="chart-color" style="background: #d32f2f;">
                                <span class="color-label">Error Red</span>
                                <span class="color-code">#d32f2f</span>
                            </div>
                            <div class="chart-color" style="background: #7b1fa2;">
                                <span class="color-label">Accent Purple</span>
                                <span class="color-code">#7b1fa2</span>
                            </div>
                            <div class="chart-color" style="background: #0097a7;">
                                <span class="color-label">Info Cyan</span>
                                <span class="color-code">#0097a7</span>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Data Visualization Best Practices</h3>
                    <div class="guidelines-list">
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Always label axes and provide units
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use tooltips to show exact values on hover
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Provide a legend when showing multiple data series
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use consistent colors for the same data types across charts
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Use 3D effects or unnecessary decorations
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Truncate Y-axis to exaggerate differences
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Use too many colors (max 6-8 distinct colors)
                            </div>
                        </div>
                    </div>

                    <div class="warning-block" style="margin-top: 32px;">
                        <strong>📊 Recommended Library:</strong> For charts and graphs, consider using Chart.js or
                        ng2-charts (Angular wrapper) for consistency with Material Design. Ensure all charts are
                        responsive and accessible.
                    </div>

                </section>
            </div>
        </mat-tab>

        <!-- TAB 11: Custom Components & Services -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">extension</mat-icon>
                Custom Components
            </ng-template>

            <div class="tab-content">
                <section>
                    <h2>Custom Components & Services</h2>
                    <p class="intro-text">
                        Lab Partner has several custom components and services built on top of Material Design.
                        These components provide consistent, reusable functionality across the application.
                    </p>

                    <h2 style="margin-top: 32px;">Dialog Service</h2>
                    <p class="specs-note">
                        <strong>Centralized Service:</strong> Use <code>DialogService</code> instead of directly opening
                        MatDialog for consistency.
                    </p>

                    <div class="file-reference">
                        <strong>📁 File:</strong> <code>src/app/shared/dialog.service.ts</code>
                    </div>

                    <h3>Available Dialog Methods</h3>
                    <div class="dialog-methods-grid">
                        <div class="dialog-method-card">
                            <h4><mat-icon>help_outline</mat-icon> Confirm Dialog</h4>
                            <p><strong>Use for:</strong> Yes/No confirmations</p>
                            <p><strong>Returns:</strong> boolean (true = confirmed)</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Usage Example</div>
                                <pre><code ngNonBindable>this.dialogService
  .openConfirmDialog('Delete this experiment?')
  .afterClosed()
  .subscribe(confirmed =&gt; &amp;#123;
    if (confirmed) &amp;#123;
      // User clicked Yes
    &amp;#125;
  &amp;#125;);</code></pre>
                            </div>
                        </div>

                        <div class="dialog-method-card">
                            <h4><mat-icon>info</mat-icon> Acknowledgment Dialog</h4>
                            <p><strong>Use for:</strong> Information messages (OK only)</p>
                            <p><strong>Returns:</strong> void</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Usage Example</div>
                                <pre><code ngNonBindable>this.dialogService
  .openAckDialog('Operation completed successfully')
  .afterClosed()
  .subscribe(() =&gt; &amp;#123;
    // User acknowledged
  &amp;#125;);</code></pre>
                            </div>
                        </div>

                        <div class="dialog-method-card">
                            <h4><mat-icon>edit_note</mat-icon> Changes Dialog</h4>
                            <p><strong>Use for:</strong> Capturing "Reason for Change"</p>
                            <p><strong>Returns:</strong> IChangeDialogResult</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Usage Example</div>
                                <pre><code ngNonBindable>this.dialogService
  .openConfirmChangesDialog(
    changes,
    experimentType,
    flags
  )
  .afterClosed()
  .subscribe(result =&gt; &amp;#123;
    if (result?.confirmed) &amp;#123;
      // Save with reason
    &amp;#125;
  &amp;#125;);</code></pre>
                            </div>
                        </div>

                        <div class="dialog-method-card">
                            <h4><mat-icon>history</mat-icon> Changelog Dialog</h4>
                            <p><strong>Use for:</strong> Viewing audit history</p>
                            <p><strong>Returns:</strong> Subscription</p>
                            <div class="code-snippet-box">
                                <div class="snippet-header">Usage Example</div>
                                <pre><code ngNonBindable>this.dialogService
  .openChangelogDialog(
    'Experiment History',
    experimentId,
    'Experiment',
    experimentId
  );</code></pre>
                            </div>
                        </div>
                    </div>

                    <h3 style="margin-top: 32px;">Dialog Configuration Options</h3>
                    <div class="config-table">
                        <table class="specs-table">
                            <thead>
                                <tr>
                                    <th>Option</th>
                                    <th>Type</th>
                                    <th>Default</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>width</code></td>
                                    <td>string</td>
                                    <td>'390px'</td>
                                    <td>Dialog width</td>
                                </tr>
                                <tr>
                                    <td><code>panelClass</code></td>
                                    <td>string</td>
                                    <td>'confirm-dialog-container'</td>
                                    <td>Custom CSS class</td>
                                </tr>
                                <tr>
                                    <td><code>isOk</code></td>
                                    <td>boolean</td>
                                    <td>false</td>
                                    <td>Show only OK button (no Cancel)</td>
                                </tr>
                                <tr>
                                    <td><code>title</code></td>
                                    <td>string</td>
                                    <td>null</td>
                                    <td>Custom dialog title</td>
                                </tr>
                                <tr>
                                    <td><code>isV2</code></td>
                                    <td>boolean</td>
                                    <td>false</td>
                                    <td>Use new dialog style</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h2 style="margin-top: 48px;">Custom Reusable Components</h2>

                    <h3>Loader Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>hourglass_empty</mat-icon>
                            <div>
                                <h4>Global Loader</h4>
                                <p>Full-screen loading overlay</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 File:</strong> <code>src/app/loader/</code>
                        </div>
                        <p><strong>Usage:</strong> Automatically shown during HTTP requests via interceptor</p>
                        <p><strong>Style:</strong> Material spinner with semi-transparent backdrop</p>
                    </div>

                    <h3 style="margin-top: 24px;">File Upload Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>cloud_upload</mat-icon>
                            <div>
                                <h4>File Upload</h4>
                                <p>Drag & drop file upload with validation</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 File:</strong> <code>src/app/file-upload/</code>
                        </div>
                        <p><strong>Features:</strong> Drag & drop, file type validation, size limits, progress
                            indication</p>
                        <p><strong>Accepted formats:</strong> Configurable per use case</p>
                    </div>

                    <h3 style="margin-top: 24px;">Column Filter Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>filter_list</mat-icon>
                            <div>
                                <h4>Column Filter</h4>
                                <p>Advanced table column filtering</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 File:</strong> <code>src/app/column-filter/</code>
                        </div>
                        <p><strong>Features:</strong> Multi-select, search, clear all, column-specific filtering</p>
                        <p><strong>Used in:</strong> Experiment List, Results tables</p>
                    </div>

                    <h3 style="margin-top: 24px;">Barcode Display Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>qr_code_2</mat-icon>
                            <div>
                                <h4>Barcode Display</h4>
                                <p>QR code and barcode generation</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 Files:</strong> <code>src/app/barcode-display/</code>,
                            <code>src/app/barcode-print/</code>
                        </div>
                        <p><strong>Features:</strong> QR code generation, print functionality, mobile-friendly</p>
                        <p><strong>Used in:</strong> Mobile barcode scanning, experiment linking</p>
                    </div>

                    <h3 style="margin-top: 24px;">Note Display Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>note</mat-icon>
                            <div>
                                <h4>Note Display</h4>
                                <p>Formatted note display with metadata</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 File:</strong> <code>src/app/note-display/</code>
                        </div>
                        <p><strong>Features:</strong> Author, timestamp, edit/delete actions, formatted text</p>
                        <p><strong>Used in:</strong> Experiment details, bundle details</p>
                    </div>

                    <h3 style="margin-top: 24px;">Cell Edit Display Component</h3>
                    <div class="component-card">
                        <div class="component-header">
                            <mat-icon>edit</mat-icon>
                            <div>
                                <h4>Cell Edit Display</h4>
                                <p>Inline table cell editing</p>
                            </div>
                        </div>
                        <div class="file-reference">
                            <strong>📁 File:</strong> <code>src/app/cell-edit-display/</code>
                        </div>
                        <p><strong>Features:</strong> Click-to-edit, validation, save/cancel, audit tracking</p>
                        <p><strong>Used in:</strong> Experiment tables, result grids</p>
                    </div>

                    <h2 style="margin-top: 48px;">Shared Services</h2>

                    <div class="services-grid">
                        <div class="service-card">
                            <h4><mat-icon>notifications</mat-icon> NotificationService</h4>
                            <div class="file-reference">
                                <strong>📁 File:</strong> <code>src/app/shared/notification.service.ts</code>
                            </div>
                            <p><strong>Purpose:</strong> Display toast notifications</p>
                            <p><strong>Methods:</strong></p>
                            <ul>
                                <li><code>showSuccess(message)</code></li>
                                <li><code>showError(message)</code></li>
                                <li><code>showWarning(message)</code></li>
                                <li><code>showInfo(message)</code></li>
                            </ul>
                        </div>

                        <div class="service-card">
                            <h4><mat-icon>web</mat-icon> DialogService</h4>
                            <div class="file-reference">
                                <strong>📁 File:</strong> <code>src/app/shared/dialog.service.ts</code>
                            </div>
                            <p><strong>Purpose:</strong> Centralized dialog management</p>
                            <p><strong>Benefits:</strong></p>
                            <ul>
                                <li>Consistent dialog styling</li>
                                <li>Standardized positioning</li>
                                <li>Audit logging integration</li>
                                <li>Type-safe dialog data</li>
                            </ul>
                        </div>

                        <div class="service-card">
                            <h4><mat-icon>science</mat-icon> ExperimentService</h4>
                            <div class="file-reference">
                                <strong>📁 File:</strong> <code>src/app/shared/experiment.service.ts</code>
                            </div>
                            <p><strong>Purpose:</strong> Experiment data operations</p>
                            <p><strong>Methods:</strong></p>
                            <ul>
                                <li><code>getExperiment(id)</code></li>
                                <li><code>saveExperiment(data)</code></li>
                                <li><code>deleteExperiment(id)</code></li>
                            </ul>
                        </div>

                        <div class="service-card">
                            <h4><mat-icon>inventory_2</mat-icon> BundleService</h4>
                            <div class="file-reference">
                                <strong>📁 File:</strong> <code>src/app/shared/bundle.service.ts</code>
                            </div>
                            <p><strong>Purpose:</strong> Results bundle operations</p>
                            <p><strong>Methods:</strong></p>
                            <ul>
                                <li><code>getBundle(id)</code></li>
                                <li><code>createBundle(data)</code></li>
                                <li><code>updateBundle(id, data)</code></li>
                            </ul>
                        </div>
                    </div>

                    <h2 style="margin-top: 48px;">Best Practices</h2>
                    <div class="guidelines-list">
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use DialogService instead of MatDialog directly
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Reuse existing custom components before creating new ones
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Follow the established patterns for dialog data and results
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="success">check_circle</mat-icon>
                            <div>
                                <strong>DO:</strong> Use NotificationService for user feedback instead of console.log
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Create duplicate dialog components - use DialogService
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Bypass the loader interceptor for HTTP requests
                            </div>
                        </div>
                        <div class="guideline-item">
                            <mat-icon class="error">cancel</mat-icon>
                            <div>
                                <strong>DON'T:</strong> Hardcode dialog widths - use DialogService defaults
                            </div>
                        </div>
                    </div>

                    <div class="warning-block" style="margin-top: 32px;">
                        <strong>🔧 Component Documentation:</strong> For detailed API documentation of each custom
                        component, refer to the component's TypeScript file and inline comments. All custom components
                        follow Angular best practices and Material Design guidelines.
                    </div>

                </section>
            </div>
        </mat-tab>

    </mat-tab-group>

</div>